import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { ArrowUpRight, ClipboardCheck, LineChart, Sparkles, Trophy } from "lucide-react";

export default function Dashboard() {
  const { user } = useAuth();
  const [dna, setDna] = useState(null);
  const [health, setHealth] = useState(null);
  const [assets, setAssets] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const [d, h, a] = await Promise.all([
          api.get("/dna"),
          api.get("/health-score/latest"),
          api.get("/assets"),
        ]);
        setDna(d.data.dna);
        setHealth(h.data.score);
        setAssets(a.data.assets || []);
      } catch (e) { /* noop */ }
    })();
  }, []);

  const completed = dna?.completed_sections?.length || 0;
  const totalSections = 11;
  const progress = Math.round((completed / totalSections) * 100);

  return (
    <div className="space-y-8">
      <div>
        <div className="label-mono mb-2">Dashboard</div>
        <h1 className="font-display text-4xl font-bold tracking-tight text-slate-950">
          Welcome, {user.full_name.split(" ")[0]}.
        </h1>
        <p className="mt-2 text-sm text-stone-600">
          {dna?.business_name ? <>Working on <span className="font-medium text-slate-950">{dna.business_name}</span>.</> : "Let's build your Business DNA™ profile to unlock every AI module."}
        </p>
      </div>

      {/* KPI ROW */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card label="Onboarding Progress" value={`${progress}%`} sub={`${completed} of ${totalSections} sections`} />
        <Card label="Business Health Score™" value={health ? `${Math.round(health.composite)}` : "—"} sub={health ? "out of 100" : "Not yet generated"} accent={health ? "emerald" : ""} />
        <Card label="Assets Generated" value={assets.length} sub="documents in library" />
        <Card label="Modules Available" value="8" sub="AI-powered agents" />
      </div>

      {/* Quick Actions */}
      <div className="grid gap-6 md:grid-cols-2">
        <div className="card-flat">
          <div className="flex items-start justify-between">
            <div>
              <div className="label-mono">Start Here</div>
              <h3 className="mt-2 font-display text-xl font-medium text-slate-950">Complete your Business DNA™</h3>
              <p className="mt-2 text-sm text-stone-600">Every AI agent gets smarter as your DNA profile fills in.</p>
            </div>
            <ClipboardCheck className="h-6 w-6 text-slate-950" strokeWidth={1.5} />
          </div>
          <div className="mt-6 h-1.5 w-full overflow-hidden rounded-full bg-stone-100">
            <div className="h-full bg-slate-950 transition-all" style={{ width: `${progress}%` }} />
          </div>
          <Link to="/app/onboarding" className="btn-primary mt-6 w-fit !text-xs !px-4 !py-2">
            Continue <ArrowUpRight className="h-3 w-3" />
          </Link>
        </div>

        <div className="card-flat">
          <div className="flex items-start justify-between">
            <div>
              <div className="label-mono">Persistent AI Advisor</div>
              <h3 className="mt-2 font-display text-xl font-medium text-slate-950">Talk to Business Einstein™</h3>
              <p className="mt-2 text-sm text-stone-600">Your AI consultant who remembers everything about your business — forever.</p>
            </div>
            <Sparkles className="h-6 w-6 text-slate-950" strokeWidth={1.5} />
          </div>
          <Link to="/app/einstein" className="btn-secondary mt-6 w-fit !text-xs !px-4 !py-2">
            Open Einstein <ArrowUpRight className="h-3 w-3" />
          </Link>
        </div>
      </div>

      {/* AI Modules */}
      <div>
        <div className="label-mono mb-4">AI Modules</div>
        <div className="grid gap-px overflow-hidden rounded-lg border border-stone-200 bg-stone-200 md:grid-cols-2 lg:grid-cols-4">
          {[
            ["Business Health Score™", "/app/health-score", "15 dimensions, radar chart, 90-day plan."],
            ["Business Plan", "/app/business-plan", "Investor-grade plan from your DNA."],
            ["Business Auditor", "/app/auditor", "SWOT, risks, and roadmap."],
            ["Marketing Plan", "/app/marketing-plan", "12-month strategy with campaigns."],
            ["Industry Intelligence", "/app/industry", "PESTLE and competitive landscape."],
            ["Pitch Outliner", "/app/pitch", "Deck outline and 10 elevator angles."],
            ["Financials", "/app/financials", "3, 5, 7 & 10-year proforma."],
            ["Capital Readiness", "/app/capital", "Funding path and investor checklist."],
          ].map(([t, to, d]) => (
            <Link key={to} to={to} className="group bg-white p-5 transition-colors hover:bg-stone-50">
              <div className="flex items-start justify-between">
                <div>
                  <div className="font-display text-base font-medium text-slate-950">{t}</div>
                  <p className="mt-2 text-xs leading-relaxed text-stone-600">{d}</p>
                </div>
                <ArrowUpRight className="h-4 w-4 text-stone-400 transition-colors group-hover:text-slate-950" />
              </div>
            </Link>
          ))}
        </div>
      </div>

      {/* Recent Assets */}
      {assets.length > 0 && (
        <div>
          <div className="label-mono mb-4">Recent Assets</div>
          <div className="rounded-lg border border-stone-200 bg-white divide-y divide-stone-200">
            {assets.slice(0, 5).map((a) => (
              <Link key={a.id} to="/app/assets" className="flex items-center justify-between p-4 transition-colors hover:bg-stone-50">
                <div>
                  <div className="text-sm font-medium text-slate-950">{a.title}</div>
                  <div className="mt-0.5 font-mono text-[10px] uppercase tracking-wider text-stone-500">{a.kind.replace(/_/g, " ")} • {new Date(a.created_at).toLocaleDateString()}</div>
                </div>
                <ArrowUpRight className="h-4 w-4 text-stone-400" />
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function Card({ label, value, sub, accent }) {
  return (
    <div className="card-flat">
      <div className="label-mono">{label}</div>
      <div className={`kpi-num mt-3 ${accent === "emerald" ? "!text-emerald-600" : ""}`}>{value}</div>
      <div className="mt-1 text-xs text-stone-500">{sub}</div>
    </div>
  );
}
